import { useState, useEffect, useRef, useCallback } from 'react';

const PDOK_SUGGEST_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest';

interface PDOKAddress {
    id: string;
    weergavenaam: string;
    postcode?: string;
    huisnummer?: number;
    straatnaam?: string;
    woonplaatsnaam?: string;
}

interface PDOKAddressSearchProps {
    onSelect: (address: { zipcode: string; houseNumber: string; street: string; displayName: string }) => void;
    placeholder?: string;
    className?: string;
    initialValue?: string;
}

const PDOKAddressSearch: React.FC<PDOKAddressSearchProps> = ({
    onSelect,
    placeholder = 'Zoek een adres...',
    className,
    initialValue = '',
}) => {
    const [query, setQuery] = useState(initialValue);
    const [suggestions, setSuggestions] = useState<PDOKAddress[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState(!!initialValue);
    const containerRef = useRef<HTMLDivElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const fetchSuggestions = useCallback(async (searchText: string) => {
        abortRef.current?.abort();

        if (searchText.trim().length < 2) {
            setSuggestions([]);
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setIsLoading(true);

        try {
            const params = new URLSearchParams({
                q: searchText,
                fq: 'type:adres',
                fl: 'id,weergavenaam,postcode,huisnummer,straatnaam,woonplaatsnaam',
                rows: '8',
            });
            const res = await fetch(`${PDOK_SUGGEST_URL}?${params}`, { signal: controller.signal });
            const data = await res.json();
            setSuggestions(data.response?.docs ?? []);
        } catch (err: unknown) {
            if (err instanceof DOMException && err.name === 'AbortError') return;
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selected) return;
        const timeout = setTimeout(() => fetchSuggestions(query), 300);
        return () => clearTimeout(timeout);
    }, [query, selected, fetchSuggestions]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (addr: PDOKAddress) => {
        const displayName = addr.weergavenaam;
        setQuery(displayName);
        setSelected(true);
        setIsOpen(false);
        setSuggestions([]);
        onSelect({
            zipcode: addr.postcode ?? '',
            houseNumber: String(addr.huisnummer ?? ''),
            street: addr.straatnaam ?? '',
            displayName,
        });
    };

    const handleInputChange = (value: string) => {
        setQuery(value);
        setSelected(false);
        if (value.length >= 2) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    return (
        <div className={`relative ${className ?? ''}`} ref={containerRef}>
            <div className="relative">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => { if (suggestions.length > 0 && !selected) setIsOpen(true); }}
                    placeholder={placeholder}
                    className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {selected && (
                    <button
                        type="button"
                        onClick={() => { setQuery(''); setSelected(false); setSuggestions([]); }}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        aria-label="Clear"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
                {isLoading && !selected && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                )}
            </div>

            {isOpen && !isLoading && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                    {suggestions.length > 0 ? (
                        suggestions.map((addr) => (
                            <div
                                key={addr.id}
                                onClick={() => handleSelect(addr)}
                                className="cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-blue-50 text-sm text-gray-800"
                            >
                                {addr.weergavenaam}
                            </div>
                        ))
                    ) : (
                        <div className="py-2 px-3 text-gray-500 text-sm">Geen resultaten gevonden</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PDOKAddressSearch;
